import { useStyle } from "@/context/StyleContext";
import React, { useMemo } from "react";
import { Text } from "@/components/text/Text";

import { Close as CloseIcon, ArrowBack } from "@mui/icons-material";

import DefaultButton from "@/components/buttons";
import { useTimmaContext } from "@/context/TimmaContext";
import SectionTitle from "@/components/misc/SectionTitle";

import { Modal, useMediaQuery, useTheme } from "@mui/material";
import { Service, SubCategory } from "@/lib/timma_salon_data";
import ModalLayout from "@/components/layout/Modal";
import ImageCard from "@/components/containers/ImageCard";
import HorizontalScrollList from "@/components/containers/HorizontalScrollList";
import { useLanguageContext } from "@/context/LanguageContext";

const Services: React.FC = () => {
  const [selectedCategory, setSelectedCategory] =
    React.useState<SubCategory | null>(null);

  const [selectedService, setSelectedService] = React.useState<Service | null>(
    null,
  );

  const { lang } = useLanguageContext();

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        paddingTop: "20px",
        paddingBottom: "20px",
      }}
    >
      <CategoryModal
        open={selectedCategory !== null}
        onClose={() => setSelectedCategory(null)}
        category={selectedCategory || undefined}
        service={selectedService || undefined}
        setSelectedService={setSelectedService}
      />
      <SectionTitle title={lang.t("landing.services.title")}>
        <Text>{lang.t("landing.services.descriptionLine1")}</Text>
      </SectionTitle>
      <CategoriesList setSelectedCategory={setSelectedCategory} />
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "30px 20px",
        }}
      >
        <DefaultButton
          onClick={() => {
            document
              .getElementById("section-book-now")
              ?.scrollIntoView({ behavior: "smooth" });
          }}
        >
          {lang.t("landing.appBar.booking")}
        </DefaultButton>
      </div>
    </div>
  );
};

export default Services;

const CategoryModal = (props: {
  category?: SubCategory;
  service?: Service;
  setSelectedService?: (service: Service | null) => void;
  open?: boolean;
  onClose?: () => void;
}) => {
  const { category, service, setSelectedService, onClose, open } = props;
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const { colors, theme } = useStyle();

  const { lang } = useLanguageContext();

  const localizeServiceName = (srv: Service) => {
    switch (lang.locale) {
      case "fi":
        return srv.nameFi || srv.nameEn;
      case "en":
        return srv.nameEn || srv.nameFi;
      case "es":
        return srv.nameEs || srv.nameEn || srv.nameFi;
      default:
        return srv.nameEn || srv.nameFi;
    }
  };

  const localizeServiceDescription = (srv: Service) => {
    switch (lang.locale) {
      case "fi":
        return srv.descriptionFi || srv.descriptionEn;
      case "en":
        return srv.descriptionEn || srv.descriptionFi;
      case "es":
        return srv.descriptionEs || srv.descriptionEn || srv.descriptionFi;
      default:
        return srv.descriptionEn || srv.descriptionFi;
    }
  };

  const localizeCategoryName = (category: SubCategory) => {
    switch (lang.locale) {
      case "fi":
        return category.nameFi || category.nameEn;
      case "en":
        return category.nameEn || category.nameFi;
      case "es":
        return category.nameEs || category.nameEn || category.nameFi;
      default:
        return category.nameEn || category.nameFi;
    }
  };

  const handleClose = () => {
    if (category && service) {
      setSelectedService?.(null);
    } else if (category) {
      onClose?.();
    }
  };

  return (
    <Modal
      open={open || false}
      disableAutoFocus
      disableEnforceFocus
      style={{ backgroundColor: "rgba(0, 0, 0, 1)", overflowY: "auto" }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose?.();
        }
      }}
    >
      <ModalLayout>
        {isMobile && (
          <div
            style={{
              position: "static",
              padding: "20px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  padding: "10px",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
              >
                <ArrowBack />
              </div>
              <div
                style={{
                  padding: "10px",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedService?.(null);
                  onClose?.();
                }}
              >
                <CloseIcon />
              </div>
            </div>
          </div>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            minHeight: "85vh",
            paddingBottom: "100px",
            paddingLeft: "20px",
            paddingRight: "20px",
          }}
        >
          {service ? (
            <SectionTitle
              title={
                localizeServiceName(service) ||
                lang.t("landing.services.serviceDetailsFallback")
              }
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  maxWidth: "680px",
                }}
              >
                <Text
                  containerStyle={{
                    paddingLeft: "20px",
                    paddingRight: "20px",
                  }}
                  style={{ flexWrap: "wrap" }}
                >
                  {localizeServiceDescription(service) || ""}
                </Text>
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                    flexDirection: isMobile ? "column" : "row",
                    justifyContent: isMobile ? "center" : "space-between",
                    alignItems: "center",
                    margin: "20px 0",
                    gap: isMobile ? "0" : "40px",
                    textAlign: "center",
                  }}
                >
                  {isMobile && (
                    <div
                      style={{
                        display: "flex",
                        width: "100%",
                        justifyContent: "space-evenly",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          flexDirection: "column",
                        }}
                      >
                        <Text uppercase>
                          {lang.t("landing.services.price")}
                        </Text>
                        <Text style={{ fontWeight: 600 }}>
                          {service.price / 100}{" "}
                          {lang.t("landing.services.euros")}
                        </Text>
                      </div>
                      <div
                        style={{
                          flexDirection: "column",
                        }}
                      >
                        <Text uppercase>
                          {lang.t("landing.services.duration")}
                        </Text>
                        <Text style={{ fontWeight: 600 }}>
                          {service.clientDuration}{" "}
                          {lang.t("landing.services.minutes")}
                        </Text>
                      </div>
                    </div>
                  )}

                  {!isMobile && (
                    <div
                      style={{
                        flexDirection: "column",
                      }}
                    >
                      <Text type="h3" style={{ marginTop: "20px" }}>
                        {lang.t("landing.services.price")}
                      </Text>
                      <Text style={{ fontWeight: 600 }}>
                        {service.price / 100} {lang.t("landing.services.euros")}
                      </Text>
                    </div>
                  )}

                  <HorizontalScrollList
                    hideScrollBar
                    fullScreenItems
                    paginate
                    navigationSettings={{
                      show: true,
                      panel: {
                        position: "inside",
                        visibility: "always",
                      },
                    }}
                    items={
                      service.photoUris || [null] // .slice(0, 3) // ?.sort(() => Math.random() - 0.5)
                    }
                    renderItem={(photo, index) => (
                      <ImageCard
                        maxWidth={"320px"}
                        noZoomEffect
                        outerContainerStyle={{
                          border: `1px solid ${colors[theme].border}`,
                          justifySelf: "center",
                        }}
                        noOverlay
                        key={`service-${service.serviceId}-photo-${index}`}
                        source={photo}
                        noSourceText={lang.t("landing.services.noPhotoYet")}
                        title={""}
                      />
                    )}
                    keyExtractor={(photo, index) =>
                      `service-${service.serviceId}-photo-${index}`
                    }
                  />

                  {!isMobile && (
                    <div
                      style={{
                        flexDirection: "column",
                      }}
                    >
                      <Text type="h3" style={{ marginTop: "20px" }}>
                        {lang.t("landing.services.duration")}
                      </Text>
                      <Text style={{ fontWeight: 600 }}>
                        {service.clientDuration}{" "}
                        {lang.t("landing.services.minutes")}
                      </Text>
                    </div>
                  )}
                </div>
                <Text
                  type="a"
                  href="#section-book-now"
                  style={{
                    textDecoration: "underline",
                  }}
                  onClick={async (e) => {
                    e.preventDefault();
                    setSelectedService?.(null);
                    onClose?.();
                    setTimeout(() => {
                      const section =
                        document.getElementById("section-book-now");
                      if (section) {
                        section.scrollIntoView({ behavior: "smooth" });
                      }
                    }, 200);
                  }}
                >
                  {lang.t("landing.services.bookCta")}
                </Text>
              </div>
            </SectionTitle>
          ) : (
            <SectionTitle
              title={
                (category && localizeCategoryName(category)) ||
                lang.t("landing.services.categoryDetailsFallback")
              }
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "12px",
                  padding: "20px",
                }}
              >
                {category?.services?.map((service, index) => (
                  <div
                    style={{
                      display: "flex",
                      flex: 1,
                      justifyContent: "center",
                    }}
                    key={`cat-${category.id}-service-${index}`}
                  >
                    <ImageCard
                      maxWidth={isMobile ? "100%" : "320px"}
                      // key={`cat-${category.id}-service-${index}`}
                      // source={service.photoUris?.[0] || randomHairImage()}
                      source={service.photoUris?.[0]}
                      title={localizeServiceName(service)}
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedService?.(service);
                      }}
                    />
                  </div>
                ))}
              </div>
            </SectionTitle>
          )}
          <DefaultButton
            style={{ pointerEvents: "auto", marginTop: "24px" }}
            onClick={handleClose}
          >
            {lang.t("landing.services.back")}
          </DefaultButton>
        </div>
      </ModalLayout>
    </Modal>
  );
};

const CategoriesList = ({
  setSelectedCategory,
}: {
  setSelectedCategory?: (category: SubCategory) => void;
}) => {
  const { subCategories } = useTimmaContext();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const { lang } = useLanguageContext();
  const localizeCategoryName = (category: SubCategory) => {
    switch (lang.locale) {
      case "fi":
        return category.nameFi || category.nameEn;
      case "en":
        return category.nameEn || category.nameFi;
      case "es":
        return category.nameEs || category.nameEn || category.nameFi;
      default:
        return category.nameEn || category.nameFi;
    }
  };

  const formattedCategories = useMemo(() => {
    return subCategories.map((category) => {
      const servicesWithPhoto = category.services.filter((service) => {
        if (service.photoUris && service.photoUris.length > 0) {
          return true;
        }
        return false;
      });
      const serviceWithPhoto =
        servicesWithPhoto.length > 0 ? servicesWithPhoto[0] : undefined;
      const src = serviceWithPhoto?.photoUris && serviceWithPhoto.photoUris[0];
      return {
        source: src,
        title: localizeCategoryName(category),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCategories, lang.locale]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: "16px",
          padding: "20px",
        }}
      >
        {formattedCategories.map((hairImage, index) => {
          return (
            <ImageCard
              outerContainerStyle={{
                flex: isMobile ? 1 : undefined,
                minWidth: isMobile ? "100%" : "320px",
              }}
              maxWidth={isMobile ? "100%" : "320px"}
              key={`catg-${index}-images-${index}`}
              source={hairImage.source}
              title={hairImage.title}
              onClick={(e) => {
                e.preventDefault();
                setSelectedCategory?.(subCategories[index]);
              }}
            />
          );
        })}
      </div>
    </div>
  );
};
